import { CheckoutPageVariant } from '@domgen/shared/enums/featureFlags';
import { gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { formatLocalisedDate } from '@domgen/shared/utils/date/formatLocalisedDate';
import { parseLocalDateString } from '@domgen/shared/utils/date/parseLocalDateString';
import { log } from '@domgen/shared/utils/log';
import { usePostHog } from '@posthog/react';
import { useEffect, useRef } from 'react';
import { FormKey } from '../../enums/formKey';
import { StepKey } from '../../enums/stepKey';
import { StoreEvent } from '../../enums/storeEvent';
import { capturePostHogEvent } from '../../services/posthog/capture';
import { useApplianceFormData } from '../../store/selectors/useApplianceFormData';
import { useAppState } from '../../store/selectors/useAppState';
import { useCheckout } from '../../store/selectors/useCheckout';
import { useGaEventData } from '../../store/selectors/useGaEventData';
import { useQuote } from '../../store/selectors/useQuote';
import { useUrlParams } from '../../store/selectors/useUrlParams';
import { store } from '../../store/store';
import { getSystemCode } from '../../utils/getSystemCode';
import { formApplianceName } from '../../utils/store/formApplianceName';
import { formCategoryName } from '../../utils/store/formCategoryName';
import {
  CheckoutFailure,
  CheckoutPartialSuccess,
  CheckoutResponseDetail,
  CheckoutSuccess,
  CheckoutTimeout,
} from './CheckoutResponses';
import { CheckoutControlLayout } from './components/CheckoutControlLayout';
import { CheckoutVariantLayout } from './components/CheckoutVariantLayout';

export function CheckoutPage() {
  const { session } = useAppState();
  const urlParams = useUrlParams();
  const formData = useApplianceFormData();
  const quote = useQuote();
  const systemCode = getSystemCode(urlParams?.rs);
  const checkout = useCheckout();
  const posthog = usePostHog();
  const element = useRef<HTMLElement | null>(null);
  const loadedCheckoutData = useRef<unknown>(null);
  const getElementRef = (el: HTMLElement | null) => {
    element.current = el;
  };
  const gaEventData = useGaEventData() || {};
  const salesWidgetClicked = gaEventData?.salesWidgetClicked;
  const applianceName = formApplianceName.get();
  const applianceCategory = formCategoryName.get();
  const {
    experimentName,
    experimentVariant,
    checkoutExperimentName,
    checkoutExperimentVariant,
    netFirstPaymentAmount: offerPrice,
  } = gaEventData;

  useEffect(() => {
    gaPageView({
      event: 'spa-pageview',
      pagename: 'registration-payment-details',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      offerPrice,
      appliancePurchaseCost: formData?.[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE],
      appliancePurchaseDate: formatLocalisedDate(
        parseLocalDateString(formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE] || ''),
        'en-US',
      ),
      modelNumberDetail: formData?.[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER],
      serialNumberDetail: formData?.[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER],
      serialNumber: formData?.[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER]
        ? 'provided'
        : 'not provided',
      modelNumber: formData?.[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER]
        ? 'provided'
        : 'not provided',
      salesWidgetClicked,
      quoteId: quote?.data?.offer?.offerReference,
      experimentName,
      experimentVariant,
      checkoutExperimentName,
      checkoutExperimentVariant,
    });
  }, []);

  const handleCheckoutEvent = <K extends Event>() => ({
    handleEvent: (checkoutResponse: K & { detail: CheckoutResponseDetail }) => {
      capturePostHogEvent(posthog, 'checkout-terms-clicked', store.get());
      if (checkoutExperimentVariant !== CheckoutPageVariant.TEST) {
        capturePostHogEvent(posthog, 'checkout-setup-protection-clicked', store.get());
      }
      switch (checkoutResponse.detail.status) {
        case 'success':
        case 'partial_success':
          store.send({
            type: StoreEvent.CHECKOUT_SUCCESS,
            data: checkoutResponse.detail as CheckoutSuccess,
          });
          handleSetupProtection();
          break;
        case 'timeout':
          log.debug({ message: 'The contracts creation timed out', data: checkoutResponse.detail });
          store.send({ type: StoreEvent.CHECKOUT_FAILED });
          break;
        case 'error':
          log.debug({
            message: 'There was an error with payment or contract creation',
            data: checkoutResponse.detail,
          });
          store.send({ type: StoreEvent.CHECKOUT_FAILED });
          break;
        default:
          log.debug({
            message: 'Something went wrong, navigating to error page',
            data: checkoutResponse.detail,
          });
          store.send({ type: StoreEvent.CHECKOUT_FAILED });
          break;
      }
    },
  });

  const handleSetupProtection = () => {
    gaEvent({
      eventCategory: 'appliance details form USA',
      eventAction: 'paymentDetails',
      eventLabel: 'Setup my protection',
      pagename: 'registration-payment-details',
      category: 'registration-pages',
      type: 'STANDARD',
      event: 'genericGAEvent',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      offerPrice,
      quoteId: quote?.data?.offer?.offerReference,
      experimentName,
      experimentVariant,
      checkoutExperimentName,
      checkoutExperimentVariant,
    });

    gaEvent({
      eventCategory: 'appliance details form USA',
      eventAction: 'paymentDetails',
      eventLabel: 'I agree to terms of use - checkbox confirmed',
      pagename: 'registration-payment-details',
      category: 'registration-pages',
      type: 'STANDARD',
      event: 'genericGAEvent',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      offerPrice,
      salesWidgetClicked,
      quoteId: quote?.data?.offer?.offerReference,
      experimentName,
      experimentVariant,
      checkoutExperimentName,
      checkoutExperimentVariant,
    });

    gaEvent({
      eventCategory: 'appliance details form USA',
      eventAction: 'paymentDetails',
      eventLabel: 'automatic renewal - checkbox confirmed',
      pagename: 'registration-payment-details',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      offerPrice,
      salesWidgetClicked,
      quoteId: quote?.data?.offer?.offerReference,
      experimentName,
      experimentVariant,
      checkoutExperimentName,
      checkoutExperimentVariant,
    });
  };

  // Effects can run twice for the same checkout data (React StrictMode in development).
  // Loading the SDK twice sets up Paysafe twice and leaves duplicate iframes in the card fields.
  useEffect(() => {
    if (checkout?.data && loadedCheckoutData.current !== checkout.data) {
      loadedCheckoutData.current = checkout.data;
      window.dngCheckout.loadCheckout({
        container: '.gc-dg-container',
        targetElementId: 'checkout-panel',
        ...checkout?.data,
      });
    }
  }, [checkout]);

  useEffect(() => {
    const checkoutSuccessHandler = handleCheckoutEvent<CheckoutSuccess>();
    const checkoutTimeoutHandler = handleCheckoutEvent<CheckoutTimeout>();
    const checkoutFailureHandler = handleCheckoutEvent<CheckoutFailure>();
    const checkoutPartialSuccessHandler = handleCheckoutEvent<CheckoutPartialSuccess>();

    window.addEventListener('dngCheckoutSuccess', checkoutSuccessHandler);
    window.addEventListener('dngCheckoutTimeout', checkoutTimeoutHandler);
    window.addEventListener('dngCheckoutFailure', checkoutFailureHandler);
    window.addEventListener('dngCheckoutPartialSuccess', checkoutPartialSuccessHandler);

    return () => {
      window.removeEventListener('dngCheckoutSuccess', checkoutSuccessHandler);
      window.removeEventListener('dngCheckoutTimeout', checkoutTimeoutHandler);
      window.removeEventListener('dngCheckoutFailure', checkoutFailureHandler);
      window.removeEventListener('dngCheckoutPartialSuccess', checkoutPartialSuccessHandler);
    };
  });

  const checkoutPanel = <div id="checkout-panel" ref={getElementRef}></div>;

  if (checkoutExperimentVariant === CheckoutPageVariant.TEST) {
    return <CheckoutVariantLayout>{checkoutPanel}</CheckoutVariantLayout>;
  }

  return (
    <CheckoutControlLayout firstName={checkout?.data.customer.firstName}>
      {checkoutPanel}
    </CheckoutControlLayout>
  );
}
